// --- Request Processors

import { parseJSON } from '../../helpers'
import { explodeFacetValues } from '../components/FilterUtils'

export function normaliseFacets(facets) {
  return (facets || []).map((facet) => {
    return {
      ...facet,
      values: explodeFacetValues(facet.field, facet.values)
    }
  })
}

export function normaliseSortOptions(options) {
  return options.map((option) => {
    return {
      ...option,
      active: option.active ?? 0
    }
  })
}

export function normaliseSwatches(result) {
  const parsedSwatches = parseJSON(result?.mfield_dotapparel21_siblings, 'array')
  if (!Array.isArray(parsedSwatches) || !parsedSwatches.length) return []

  return parsedSwatches
    // .filter((swatch) => swatch.published) -- TODO: Reenable this when the products are published
    .sort((swatchA, swatchB) => {
      if (swatchA.isCurrent === swatchB.isCurrent) return 0
      if (swatchA.isCurrent) return -1
      return 1
    })
}

export function getAthosVariantRecords(result) {
  const variants = result?.variants
  if (Array.isArray(variants) && variants.length) return variants
  if (Array.isArray(variants?.data) && variants.data.length) return variants.data
  return []
}

export function adaptAthosVariants(result) {
  const variantData = getAthosVariantRecords(result)
  if (!variantData.length) return null

  return variantData.map((entry) => {
    const nested = entry?.data && typeof entry.data === 'object' && !Array.isArray(entry.data) ? entry.data : {}
    const core = entry?.mappings?.core ?? nested?.mappings?.core ?? {}
    const options = entry?.options ?? nested?.options ?? {}
    const attributes = { ...nested?.attributes, ...entry?.attributes }

    const flatOptions = {}
    for (const [key, value] of Object.entries(options)) {
      flatOptions[key] = value?.value ?? value
    }

    return {
      ...nested,
      ...attributes,
      ...core,
      ...flatOptions,
      id: core.uid ?? entry.uid ?? nested.uid ?? attributes.uid ?? attributes.id ?? entry.id ?? entry.value,
      price: core.price ?? attributes.price ?? result.price,
      compare_at_price: core.msrp ?? attributes.compare_at_price ?? result.compare_at_price ?? result.msrp,
      available: core.available ?? attributes.available ?? entry.available,
      options,
      optionConfig: entry.optionConfig ?? nested.optionConfig,
      badges: entry.badges ?? nested.badges
    }
  })
}

function synthesiseVariant(result) {
  const hasAvailability =
    result.available === true ||
    result.available === false ||
    result.available === 'true' ||
    result.available === 'false'

  return {
    ...result,
    id: result.uid ?? result.id,
    price: result.price,
    compare_at_price: result.compare_at_price ?? result.msrp,
    available: hasAvailability ? result.available === true || result.available === 'true' : undefined,
    title: result.name
  }
}

export function getFeedSizes(result) {
  const raw = result?.sizes ?? result?.ss_sizes ?? result?.variant_sizes
  const sizes = parseJSON(raw, 'array')
  return Array.isArray(sizes) && sizes.length ? sizes : null
}

function adaptSizesFromFeed(result) {
  const sizes = getFeedSizes(result)
  if (!sizes) return null

  return sizes.map((size) => ({
    id: size.variant_id,
    title: size.label,
    option1: size.label,
    available: size.available === undefined ? undefined : Boolean(Number(size.available)),
    uk: size.uk || size.label,
    us: size.us || '',
    eu: size.eu || '',
    cm: size.cm || '',
    width: size.width || '',
    age_group: size.age_group || ''
  }))
}

function mergeVariantLists(primary, secondary) {
  if (!secondary?.length) return primary
  const byId = new Map(secondary.map((variant) => [String(variant.id), variant]))
  return primary.map((variant) => ({
    ...byId.get(String(variant.id)),
    ...variant
  }))
}

/**
 * Athos discovery is variant-first: `uid` is the matching SKU, `parentId`/`groupId`
 * is the Shopify product. `variants.data` is often a single row, so a non-empty
 * list is not a complete size set. Prefer the feed `sizes` blob (formerly
 * `ss_sizes` / `variant_sizes`) when it has more variant ids than the native object.
 */
function resolveVariants(result) {
  const athosVariants = adaptAthosVariants(result)
  const feedSizes = adaptSizesFromFeed(result)

  if (feedSizes?.length && (!athosVariants || athosVariants.length < feedSizes.length)) {
    return mergeVariantLists(feedSizes, athosVariants)
  }

  if (athosVariants?.length) return athosVariants

  const legacyVariants = parseJSON(result.variants, 'array')
  if (Array.isArray(legacyVariants) && legacyVariants.length) return legacyVariants

  if (feedSizes?.length) return feedSizes

  return [synthesiseVariant(result)]
}

export function getProductParentId(result) {
  return result?.parentId ?? result?.groupId ?? result?.parent_id ?? null
}

function resolveImages(result) {
  if (result.images || result.images_alt) {
    return normaliseImages(result) ?? []
  }

  const srcs = [result.imageUrl, result.parentImageUrl].filter(Boolean)
  const uniqueSrcs = [...new Set(srcs)]
  return uniqueSrcs.map((src) => ({ src, alt: result.name ?? '' }))
}

export function normalisedArray(value) {
  if (typeof value === 'string' && value.includes('|')) {
    return value.split('|')
  } else if (typeof value === 'string') {
    return [value]
  }
  return value
}

export function normaliseImages(result) {
  const srcs = normalisedArray(result.images)
  const alts = normalisedArray(result.images_alt)

  if (!Array.isArray(srcs)) return []

  return srcs.map((src, index) => {
    return {
      src,
      alt: Array.isArray(alts) ? alts[index] : ''
    }
  })
}

/**
 * Athos indexes each product at its first variant's URL, and a PDP opened on `?variant=` preselects
 * that size. Product links must land unselected, so the product-level URL drops the param.
 */
function productUrl(url) {
  if (typeof url !== 'string' || !url.includes('variant=')) return url
  const parsed = new URL(url, window.location.origin)
  parsed.searchParams.delete('variant')
  const path = parsed.pathname + parsed.search + parsed.hash
  return /^https?:/i.test(url) ? parsed.origin + path : path
}

export function normaliseResults(results) {
  if (!results) return []
  return results.map((result) => {
    const swatches = normaliseSwatches(result)
    const images = resolveImages(result)
    const variants = resolveVariants(result)

    return {
      ...result,
      url: productUrl(result.url),
      image: result.image ?? result.imageUrl,
      parentId: result.parentId ?? result.groupId ?? result.parent_id,
      images,
      swatches,
      variants
    }
  })
}

export function normaliseRecommendations(results) {
  const responseId = results?.responseId
  const normalisedResults = results?.results?.map((result) => {
    const merged = {
      ...result,
      ...result.attributes,
      ...result.mappings?.core
    }

    const swatches = normaliseSwatches(result.attributes || result)
    const variants = resolveVariants(merged)
    const images = resolveImages(merged)

    return {
      ...merged,
      responseId,
      url: productUrl(merged.url),
      image: merged.image ?? merged.imageUrl,
      parentId: merged.parentId ?? merged.groupId ?? merged.parent_id,
      images,
      variants,
      swatches
    }
  })

  if (!normalisedResults || !normalisedResults.length) return []
  return normalisedResults
}
